
import React, { useState } from 'react';
import { Shield, AlertTriangle, FileText, Zap } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';

// Import our components
import GovernancePolicies from '@/components/policies/GovernancePolicies';
import NonCompliantGroups from '@/components/policies/NonCompliantGroups';
import { PolicyExecutorSimulation } from '@/components/policies/simulation/PolicyExecutorSimulation';
import { policies } from '@/components/policies/PolicyData';
import { useGraphTeams } from '@/hooks/useGraphTeams';

const Policies = () => {
  const [selectedTab, setSelectedTab] = useState('policies');
  const { teams, loading, fetchTeams } = useGraphTeams();
  const isMobile = useIsMobile();
  
  // Fetch teams when the component mounts or when the non-compliant tab is selected
  React.useEffect(() => {
    if (selectedTab === 'violations' && teams.length === 0 && !loading) {
      fetchTeams();
    }
  }, [selectedTab, teams.length, loading, fetchTeams]);
  
  // Transform Graph teams to non-compliant format
  // Only include teams with compliance < 80
  const nonCompliantTeams = React.useMemo(() => {
    return teams
      .filter(team => team.compliance && team.compliance < 80)
      .map(team => ({
        id: parseInt(team.id.replace(/\D/g, '')) || Math.floor(Math.random() * 10000),
        name: team.displayName,
        owner: "Graph API Owner",
        violations: [
          team.compliance && team.compliance < 60 ? 'Critical Compliance' : 'Low Compliance',
          team.isArchived ? 'Archived Team' : 'Active Team Issues',
          'Governance Policy Violation'
        ],
        severity: team.compliance && team.compliance < 60 ? 'Critical' : 
                team.compliance && team.compliance < 70 ? 'High' : 'Medium',
        created: new Date(Date.now() - Math.random() * 10000000000).toISOString().split('T')[0]
      }));
  }, [teams]);
  
  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold animate-fade-in">Governance Policies</h1>
        <p className="text-muted-foreground animate-fade-in opacity-0" style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
          Monitor compliance and manage governance policies
        </p>
      </div>
      
      <Tabs defaultValue="policies" className="animate-fade-in opacity-0" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
        <TabsList className={`${isMobile ? 'flex overflow-x-auto mobile-tab-scroll' : 'grid grid-cols-3'} w-full mb-6`}>
          <TabsTrigger 
            value="policies" 
            onClick={() => setSelectedTab('policies')}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <FileText className="h-4 w-4" />
            <span>Governance Policies</span>
          </TabsTrigger>
          <TabsTrigger 
            value="violations" 
            onClick={() => setSelectedTab('violations')}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <AlertTriangle className="h-4 w-4" />
            <span>Non-Compliant Groups</span>
          </TabsTrigger>
          <TabsTrigger 
            value="executor" 
            onClick={() => setSelectedTab('executor')}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <Zap className="h-4 w-4" />
            <span>Policy Executor</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Governance Policies Tab */}
        <TabsContent value="policies">
          <GovernancePolicies policies={policies} />
        </TabsContent>
        
        {/* Non-Compliant Groups Tab */}
        <TabsContent value="violations">
          <NonCompliantGroups groups={nonCompliantTeams} loading={loading} />
        </TabsContent>
        
        {/* Policy Executor Tab */}
        <TabsContent value="executor">
          <PolicyExecutorSimulation />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default Policies;
